// useKeyboardHandlers

import chroma from 'chroma-js';
import { useEffect, useCallback } from 'react';
import useStore from '../../store';

import {
  handleToolSwitch,
  handleLayerSelection,
  handleMenuToggle,
  handleUserSetsToggle,
  handleModifierKeys,
  handleUndoRedo,
  handleLineWidth,
  handleVisibilityAndPaste,
  handleNavigation,
  handleResetZoom,
  handleCloseMenu,
} from './keyboardHandlers';

export const useKeyboardHandlers = (ref) => {

  const { telegram, menu, work, drawing, info, convy, brush, history } = ref;
  const { game, user } = info;
  const { mouse, keyboard, zoom } = convy;

  const {
    toggleMenu, 
    closeMenu,
    showPopupMessage,
  } = menu.methods;
  const {
    saveUserSets, 
  } = work.methods;
  const {
    changeZoomPc,
    changeLayerScalePc,
    resetZoom,
  } = drawing.methods;
  const {
    changeTool,
    setTheSizeFun,
    setTheColorFun,
  } = brush.methods;
  const {
    undoLastAction,
    redoLastAction,
  } = history.methods;


  // --- HELPER: Проверка, рисует ли мафия ---

  const ifMNotDrawing = () => {

    if (info?.game?.mode === 'arcade') { return true; }
    if (info?.game?.mode !== 'mafia') { return false; }

    const { telegramUserId } = telegram;
    const { mafiaPublic } = info?.game
    const { mafia } = ref;

    if (!mafia.team) {return true}
    if (mafia.team === 'town' && mafia.phase !== 'draw_civilian') { return true; }
    if (mafia.team === 'mafia' && ref.mafia?.activeTab !== 'draw') { return true; }

    const iAmDead = mafiaPublic?.alive && !mafiaPublic?.alive?.[telegramUserId];
    if (iAmDead) { return true; }

    return false;


  };


  const lineWidthControl = (delta) => {

    const lineWidth = useStore.getState().lineWidth;


    let stepSize = Math.round(Math.cbrt(lineWidth));
    let minimalLineWidth = 1;

    if (lineWidth === 1 && delta < 0) {
      // Do nothing
    } else if (lineWidth <= 1) {
      stepSize = 0.2
      minimalLineWidth = 0.2;
    }

    let newLineWidth = lineWidth + (delta > 0 ? -stepSize : stepSize);

    newLineWidth = Math.max(minimalLineWidth, newLineWidth);
    
    setTheSizeFun(newLineWidth);

  }

  const handleKeyDown = useCallback((event) => {
    // 1. Блокировка для чата Мафии
    if (ifMNotDrawing()) return;

    const activeTool = useStore.getState().activeTool;

    if (event.target.closest('.input-field')) return;

    handleToolSwitch(event, { changeTool });
    
    handleMenuToggle(event, { toggleMenu });
    handleUserSetsToggle(event, { user, saveUserSets });
    handleModifierKeys(event, { keyboard, activeTool, brush });
    handleUndoRedo(event, { undoLastAction, redoLastAction });
    handleLineWidth(event, { setTheSizeFun });
    handleVisibilityAndPaste(event, ref);
    handleNavigation(event, { convy, drawing });
    handleResetZoom(event);
    handleCloseMenu(event, { closeMenu });

    useStore.setState(state => ({ keyPressed: state.keyPressed + 1 }));

  }, []);

  const handleKeyUp = useCallback((event) => {
    // 2. Блокировка для чата Мафии
    if (ifMNotDrawing()) return;

    useStore.setState(state=>({keyPressed: state.keyPressed + 1}));

    if (event.target.closest('.input-field')) { return; }

    const activeTool = useStore.getState().activeTool;

    
    if (event.key === 'Alt') {
      keyboard.altIsDown = false;
      if (activeTool === 'pipette') {
        useStore.setState({
          pipettePicking: false,
          activeTool: brush.previousTool,
        })
      }
    }

    if (event.key === 'Control') {
      event.preventDefault();
      keyboard.controlIsDown = false;
      useStore.setState( { zoomMode: false });

      if(convy.layerTransformation?.type === 'keys') {
        drawing.methods.finishLayerTransform();
      }
    }

    if (event.code === 'Space') {
      event.preventDefault();
      keyboard.spaceIsDown = false;
      useStore.setState( { zoomMode: false });
    }

    if (event.code === 'Tab') {
      event.preventDefault();
      keyboard.tabIsDown = false;
    }

    if (event.key === 'Shift') {
      keyboard.shiftIsDown = false;
      useStore.setState( { showPalette: false });
    }

  }, []);


  const handleColorWheel = (delta, keyboard) => {
    const color = useStore.getState().color;

    if (keyboard.altIsDown && !keyboard.controlIsDown) {
      const currentColor = chroma(color);

      const initialSaturation = currentColor.get('hsv.s');
      const initialHue = currentColor.get('hsv.h');
      const currentBrightness = currentColor.get('hsv.v');
      const currentAlpha = currentColor.alpha();  
      
      let brightnessDelta = -delta * 0.0003;
      
      let newBrightness = Math.max(0.01, Math.min(1, currentBrightness + brightnessDelta));
      
      let newColor = currentColor;
      newColor = newColor.set('hsv.v', newBrightness);
      
      newColor = newColor.set('hsv.s', initialSaturation);
      newColor = newColor.set('hsv.h', initialHue);
      
      newColor = newColor.alpha(currentAlpha);
      
      let finalBrightness = chroma(newColor).get('hsv.v');
      if (!finalBrightness) {return;}
      
      setTheColorFun(newColor.hex());
      brush.quickColorChangeTime = Date.now();
    } else if (keyboard.shiftIsDown) {
      let newColor = chroma(color).alpha(Math.max(0, Math.min(1, chroma(color).alpha() + delta * 0.0005))).hex('rgba');
      setTheColorFun(newColor);
      brush.quickColorChangeTime = Date.now();
    }
  };

  const handleReferenceWheel = (event, delta, ref) => {
    const source = ref.menu.source;
    if (!source) return;
    const rect = event.target.closest('.reference-image-wrapper').getBoundingClientRect();
    const pos = { 
      x: event.clientX - rect.left - rect.width / 2,  
      y: event.clientY - rect.top - rect.height / 2,
    };
    const changeAmount = (delta > 0 ? -0.1 : 0.1);
    let newZoomScale = source.zoomScale + (source.zoomScale * changeAmount);
    const fitScale = Math.min(source.displayWidth / source.naturalWidth, source.displayHeight / source.naturalHeight);
    newZoomScale = Math.max(fitScale, newZoomScale);
    newZoomScale = Math.min(10, newZoomScale);

    if (newZoomScale > fitScale) {
      source.zoomModified = true;
    }

    const imagePointX = (pos.x - source.panOffsetX) / source.zoomScale;
    const imagePointY = (pos.y - source.panOffsetY) / source.zoomScale;

    const newOffsetX = pos.x - imagePointX * newZoomScale;
    const newOffsetY = pos.y - imagePointY * newZoomScale;

    source.zoomScale = newZoomScale;
    source.panOffsetX = newOffsetX;
    source.panOffsetY = newOffsetY;

    if (Math.abs(newZoomScale - fitScale) < 0.01) {
      source.zoomScale = fitScale;
      source.zoomModified = false;
      source.displayHeight = source.displayWidth * source.aspectRatio;
      const fitScaleNew = Math.min(source.displayWidth / source.naturalWidth, source.displayHeight / source.naturalHeight);
      source.panOffsetX = (source.displayWidth - source.naturalWidth * fitScaleNew) / 2;
      source.panOffsetY = (source.displayHeight - source.naturalHeight * fitScaleNew) / 2;
    }
  };


  const handleWheel = useCallback((event) => {
    // 3. Блокировка для чата Мафии (ВАЖНО: до preventDefault)
    // Если чат открыт - мы просто выходим, позволяя браузеру скроллить чат
    if (ifMNotDrawing()) return;

    if (event.target.closest('.add-scroll')) { return; }
    if (event.target.closest('.scroll-active')) { return; }
    if (event.target.closest('.premium-menu')) { return; }
  
    useStore.setState(state => ({ keyPressed: state.keyPressed + 1 }));
    event.preventDefault();
  
    const color = useStore.getState().color;
    const delta = event.deltaY > 0 ? 150 : -150;
  
    if (keyboard.altIsDown && !keyboard.controlIsDown) {
  
      handleColorWheel(delta, keyboard);
  
    } else if (keyboard.shiftIsDown) {
  
      let newColor = chroma(color).alpha(Math.max(0, Math.min(1, chroma(color).alpha() + delta * 0.0005))).hex('rgba');
      setTheColorFun(newColor);
      brush.quickColorChangeTime = Date.now();
  
    } else if (keyboard.controlIsDown) {
  
      if (event.target.closest('.reference-image-wrapper')) {
        handleReferenceWheel(event, delta, ref);
      } else {
        if (keyboard.altIsDown) {
          changeLayerScalePc(delta)
        } else {
          changeZoomPc(delta);
        }
      }
  
    } else {
  
      lineWidthControl(delta);
  
    }
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    // passive: false важно для preventDefault, но для скролла чата мы не вызываем preventDefault, так что все ок
    window.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('wheel', handleWheel);
    };
  }, [handleKeyDown, handleKeyUp, handleWheel]);

  return {
  };
};



